import React from 'react';
import { useIntl } from 'react-intl';
import MaterialTable, { MaterialTableProps } from '@material-table/core';
import { TablePagination } from '@mui/material';

type Props<T extends object> = MaterialTableProps<T>;

const LocalizedMaterialTable = <T extends object>({
  data,
  title,
  actions,
  columns,
  options,
  components,
  parentChildData,
}: Props<T>) => {
  const intl = useIntl();

  const localization = {
    body: {
      emptyDataSourceMessage: intl.formatMessage({ id: 'tables.no.records' }),
    },
    pagination: {
      labelRowsPerPage: intl.formatMessage({ id: 'tables.rows.per.page' }),
      firstTooltip: intl.formatMessage({ id: 'tables.first.page.tooltip' }),
      previousTooltip: intl.formatMessage({ id: 'tables.previous.page.tooltip' }),
      nextTooltip: intl.formatMessage({ id: 'tables.next.page.tooltip' }),
      lastTooltip: intl.formatMessage({ id: 'tables.last.page.tooltip' }),
    },
    toolbar: {
      searchTooltip: intl.formatMessage({ id: 'tables.search' }),
      searchPlaceholder: intl.formatMessage({ id: 'tables.search' }),
    },
  } as any;

  const normalize = (value: string) => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const getColumnNestedValue = (object: T, path: string) => {
    return path.split('.').reduce<any>((value, key) => { return value?.[key] }, object);
  };

  const localizedColumns = columns.map((column) => {
    // keep custom sorting of column
    if (column.customSort || typeof column.field !== 'string') return column;

    return {
      ...column,
      customSort: (a: T, b: T) => {
        const aValue = getColumnNestedValue(a, column.field as string);
        const bValue = getColumnNestedValue(b, column.field as string);

        // empty/null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return -1;
        if (bValue == null) return 1;

        // case-insensitive and accent-insensitive sorting for strings
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return normalize(aValue).localeCompare(normalize(bValue),
            undefined,
            { sensitivity: 'base' }
          );
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') return aValue - bValue;

        return String(aValue).localeCompare(String(bValue), undefined, { sensitivity: 'base' });
      },
    };
  });

  return (
    <React.Fragment>
      <MaterialTable
        title={title}
        columns={localizedColumns}
        data={data}
        options={options}
        actions={actions}
        parentChildData={parentChildData}
        localization={localization}
        components={{
          ...components,
          Pagination: (props) => (
            <TablePagination
              {...props}
              labelRowsPerPage={intl.formatMessage({ id: 'tables.rows.per.page' })}
              labelDisplayedRows={({ from, to, count }) =>
                intl.formatMessage(
                  { id: 'tables.displayed.rows' },
                  {
                    from,
                    to,
                    count: count !== -1 ? count : `more than ${to}`,
                  }
                )
              }
              SelectProps={{
                ...props.SelectProps,
                renderValue: (value) => value, //shows "10" instead of "10 rows"
              }}
            />
          ),
        }}
      />
    </React.Fragment>
  );
};

export default LocalizedMaterialTable;
